import { useEffect, useRef, useState } from "react";
import UIkit from "uikit";
import { openGuidePhotoViewer } from "../../../utils/guidePhotoViewer";

const Photo = ({ photo, index, total, onPreview, onRemove, disabled }) => {
  const [preview, setPreview] = useState(photo.url);
  useEffect(() => {
    if (!photo.file) {
      setPreview(
        photo.url?.replace(
          "http://uploads.ruebarue.com",
          "https://public.ruebarue.com"
        )
      );
      return;
    }
    const url = URL.createObjectURL(photo.file);
    setPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [photo]);
  const name = photo.original_name || photo.name || "Photo";
  return (
    <div style={{ width: 110, position: "relative" }}>
      <a
        href={preview}
        data-guide-photo
        aria-label={`View photo ${index + 1} of ${total}`}
        title="View larger photo"
        onClick={(event) => {
          event.preventDefault();
          event.stopPropagation();
          onPreview(index, event.currentTarget);
        }}
        style={{ display: "block", cursor: "zoom-in" }}
      >
        <img
          src={preview}
          alt={name}
          style={{ width: 110, height: 88, objectFit: "cover", borderRadius: 6 }}
        />
      </a>
      <div className="uk-text-truncate" title={name}>
        {name}
      </div>
      <button
        type="button"
        className="guide-photo-remove"
        disabled={disabled}
        onClick={onRemove}
        aria-label={`Remove ${name}`}
        title={`Remove ${name}`}
        style={{
          position: "absolute",
          top: 4,
          right: 4,
          width: 32,
          height: 32,
          padding: 0,
          border: 0,
          borderRadius: "50%",
          background: "#fff",
          cursor: disabled ? "not-allowed" : "pointer",
          opacity: disabled ? 0.5 : 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <img
          src="/images/delete.svg"
          alt=""
          aria-hidden="true"
          width="28"
          height="28"
        />
      </button>
    </div>
  );
};

export default function Photos({ photos = [], onChange, disabled }) {
  const fileInput = useRef(null);
  const gallery = useRef(null);
  const viewer = useRef(null);
  useEffect(() => () => {
    viewer.current?.hide();
    viewer.current = null;
  }, [photos]);
  const previewPhoto = (index, trigger) => {
    const items = Array.from(gallery.current.querySelectorAll("[data-guide-photo]")).map((anchor) => ({
      url: anchor.href,
      alt: anchor.querySelector("img").alt,
    }));
    if (viewer.current) return;
    const panel = openGuidePhotoViewer(window.UIkit || UIkit, items, index, trigger, () => {
      if (viewer.current === panel) viewer.current = null;
    });
    viewer.current = panel;
  };
  const uploadButton = (
    <button
      type="button"
      className="uk-button uk-button-default"
      disabled={disabled}
      onClick={() => fileInput.current?.click()}
      style={{
        width: "auto",
        minHeight: 36,
        color: "var(--dark-purple)",
        background: "#fff",
      }}
    >
      Add photos
    </button>
  );
  return (
    <div className="uk-width-1-1" style={{ marginBottom: photos.length ? 8 : 20 }}>
      <div className="uk-form-label">Photos</div>
      {photos.length ? (
        <>
          <div
            ref={gallery}
            style={{ display: "flex", flexWrap: "wrap", gap: 12, marginBottom: 12 }}
          >
            {photos.map((photo, index) => (
              <Photo
                key={photo.id || `${photo.url || photo.name}-${index}`}
                photo={photo}
                index={index}
                total={photos.length}
                onPreview={previewPhoto}
                disabled={disabled}
                onRemove={() => onChange(photos.filter((_, i) => i !== index))}
              />
            ))}
          </div>
          {uploadButton}
        </>
      ) : (
        <div
          style={{
            border: "1px dashed #BFCFDD",
            borderRadius: 6,
            padding: 20,
            textAlign: "center",
            background: "#F8FAFC",
            marginTop: 8,
          }}
        >
          <img
            src="/images/upload-image.svg"
            alt=""
            aria-hidden="true"
            width="40"
            height="40"
          />
          <p style={{ color: "var(--dark-purple)", margin: "8px 0 4px" }}>
            No photos yet
          </p>
          <p style={{ color: "#687786", margin: "0 0 12px" }}>
            Add photos of the equipment or controls guests need.
          </p>
          {uploadButton}
        </div>
      )}
      <input
        ref={fileInput}
        type="file"
        multiple
        accept="image/*"
        disabled={disabled}
        aria-label="Add photos"
        style={{ display: "none" }}
        onChange={(event) => {
          const added = Array.from(event.target.files || []).map((file) => ({
            file,
            name: file.name,
            original_name: file.name,
            type: "image",
          }));
          onChange([...photos, ...added]);
          event.target.value = "";
        }}
      />
    </div>
  );
}
